import * as React from 'react';
import { FindHit } from './find-engine';
import { FieldStatus, MatchedField } from './use-locate';

function snippetNodes(hit: FindHit): React.ReactNode {
    const s = hit.snippet;
    const a = hit.snippetMatchStart;
    const b = hit.snippetMatchEnd;

    if (a == null || b == null || a < 0 || b > s.length || a >= b) {
        return s;
    }
    return (
        <span className="findResultSnippet">
            {s.slice(0, a)}
            <mark>{s.slice(a, b)}</mark>
            {s.slice(b)}
        </span>
    );
}

function hitMatchText(hit: FindHit): string {
    const s = hit.snippet || '';
    const a = hit.snippetMatchStart;
    const b = hit.snippetMatchEnd;

    if (a == null || b == null || a < 0 || b > s.length || a >= b) {
        return (s || '').replace(/\s+/g, ' ').trim();
    }
    return s.slice(a, b).replace(/\s+/g, ' ').trim();
}

export function LocatePanel(props: {
    filePager?: {
        label: string;
        current: number;
        total: number;
        canPrev: boolean;
        canNext: boolean;
        loading?: boolean;
        onPrev: () => void;
        onNext: () => void;
    };
    mimeKind: string;
    canSearch: boolean;
    locatePending: boolean;
    locateUnavailable: boolean;
    allowOcr: boolean;
    ocrRunning: boolean;
    aligning: boolean;
    ocrProgress: number;
    ocrNote: string;
    displayUrl: string;
    hasPdf: boolean;
    hasText: boolean;
    textReady: boolean;
    customQuery: string;
    setCustomQuery: (q: string) => void;
    runCustomSearch: () => void;
    runScan: () => void;
    searchStatus: string;
    locateSource: 'field' | 'custom' | null;
    matchedLabel: string;
    verifyProgressLabel: string;
    requiredFields: MatchedField[];
    supportingFields: MatchedField[];
    orphanSupporting: MatchedField[];
    hits: FindHit[];
    activeIndex: number;
    activeFieldId: string | null;
    activeLabel: string;
    pageCount: number;
    selectMatchedField: (field: MatchedField) => void;
    activateHit: (index: number, focus: boolean) => void;
    collectSoftHits: (helpers: MatchedField[]) => { hits: FindHit[]; truncated: boolean };
    activateLikelyHit: (parent: MatchedField, helpers: MatchedField[], hitIndex: number) => void;
}) {
    const searchInputRef = React.useRef<HTMLInputElement>(null);
    const showPageLabels = props.pageCount > 1;
    const pageLabel = (pageNumber: number) => (showPageLabels && pageNumber > 0 ? `Page ${pageNumber}` : '');

    const fieldStatusText = (status: FieldStatus, field: MatchedField, isHelper: boolean): string => {
        if (status === 'pending') {
            return 'Checking…';
        }
        if (status === 'unavailable') {
            return 'Not Found';
        }
        if (status === 'found' || status === 'likely') {
            return isHelper
                ? (field.hint.detail || 'On this page')
                : (field.hint.detail || field.hint.value);
        }
        return 'Not on this page';
    };

    const renderFieldRow = (field: MatchedField, opts?: {
        helper?: boolean;
        status?: FieldStatus;
        onActivate?: () => void;
        countOverride?: number;
        rowKey?: string;
        labelOverride?: string;
        valueOverride?: string;
        hideCount?: boolean;
        activeToken?: string;
        titleOverride?: string;
    }) => {
        const hitPage = field.hits[0] ? field.hits[0].pageIndex + 1 : (field.hint.pageHint || 0);
        const activeToken = opts?.activeToken || field.hint.id;
        const isActive = props.activeFieldId === activeToken && props.locateSource === 'field';
        const isHelper = !!opts?.helper;
        const status: FieldStatus = opts?.status
            || (props.locatePending
                ? 'pending'
                : (props.locateUnavailable
                    ? 'unavailable'
                    : (field.count > 0 ? 'found' : 'missing')));
        const displayCount = opts?.countOverride != null
            ? opts.countOverride
            : field.count;
        const canActivate = status === 'likely'
            || (props.canSearch && (field.count > 0 || !!opts?.onActivate));
        const pageSuffix = (status === 'found' || status === 'likely') && hitPage && !opts?.labelOverride
            ? pageLabel(hitPage)
            : '';
        const expectedValue = (field.hint.detail || field.hint.value || '').trim();
        const showExpected = !isHelper
            && !!expectedValue
            && (status === 'unavailable' || status === 'missing' || status === 'likely');
        return (
            <button
                key={opts?.rowKey || field.hint.id}
                type="button"
                className={[
                    'dvFieldRow',
                    isHelper ? 'dvFieldRowHelper' : '',
                    isActive ? 'dvFieldRowActive' : '',
                    status === 'pending' ? 'dvFieldRowPending' : '',
                    status === 'likely' ? 'dvFieldRowLikely' : '',
                    status === 'missing' || status === 'unavailable' ? 'dvFieldRowMiss' : ''
                ].filter(Boolean).join(' ')}
                onClick={() => {
                    if (opts?.onActivate) {
                        opts.onActivate();
                        return;
                    }
                    props.selectMatchedField(field);
                }}
                disabled={!canActivate || status === 'pending' || status === 'unavailable' || status === 'missing'}
                title={opts?.titleOverride || field.hint.detail || field.hint.value || undefined}
            >
                <span className="dvFieldMain">
                    <span className="dvFieldLabel">
                        {opts?.labelOverride || field.hint.label}
                        {pageSuffix ? ` · ${pageSuffix}` : ''}
                    </span>
                    <span className="dvFieldValue">
                        {opts?.valueOverride || fieldStatusText(status, field, isHelper)}
                    </span>
                    {showExpected ? (
                        <span className={field.hint.id === 'name' ? 'dvFieldExpected dvFieldExpectedName' : 'dvFieldExpected'}>
                            {expectedValue}
                        </span>
                    ) : null}
                </span>
                {!opts?.hideCount && (
                    <span className="dvFieldCount">
                        {status === 'pending' ? '…' : (displayCount > 0 ? displayCount : '—')}
                    </span>
                )}
            </button>
        );
    };

    const renderFieldGroup = (field: MatchedField) => {
        const helpers = (!props.locatePending && field.count === 0)
            ? props.supportingFields.filter(s => s.hint.relatedTo === field.hint.id)
            : [];
        const likelyHelpers = helpers.filter(h => h.count > 0);
        const softHits = props.collectSoftHits(likelyHelpers).hits;
        const valueHits = softHits.filter(h => h.locateRole !== 'label');
        const status: FieldStatus = props.locatePending
            ? 'pending'
            : (props.locateUnavailable
                ? 'unavailable'
                : (field.count > 0
                    ? 'found'
                    : (likelyHelpers.length > 0 ? 'likely' : 'missing')));
        if (status === 'likely') {
            const entries = valueHits.length > 0
                ? softHits
                    .map((hit, hitIndex) => ({ hit, hitIndex }))
                    .filter(({ hit }) => hit.locateRole !== 'label')
                : softHits
                    .map((hit, hitIndex) => ({ hit, hitIndex }))
                    .filter(({ hit }) => hit.locateRole === 'label');
            const isSsnField = field.hint.id === 'ssn'
                || likelyHelpers.some(h => h.hint.locateKind === 'ssnPattern');
            const headText = 'Not found';
            const headActivateIndex = entries[0] ? entries[0].hitIndex : 0;
            return (
                <div key={field.hint.id} className="dvFieldGroup dvFieldGroupLikely">
                    {renderFieldRow(field, {
                        status: 'likely',
                        rowKey: `${field.hint.id}:likely-head`,
                        activeToken: `${field.hint.id}:likely:${headActivateIndex}`,
                        valueOverride: headText,
                        titleOverride: isSsnField
                            ? 'Expected SSN not found on this page'
                            : 'Expected value not found on this page',
                        hideCount: true,
                        onActivate: () => props.activateLikelyHit(field, likelyHelpers, headActivateIndex)
                    })}
                    {entries.length > 0 ? (
                        <div className="dvFieldHelpers">
                            <div className="dvFieldHelpersLabel">
                                {valueHits.length > 0
                                    ? (isSsnField ? 'Pattern match' : 'Format match')
                                    : 'Field label on page'}
                            </div>
                            {entries.map(({ hit, hitIndex }, i) => {
                                const matchText = hitMatchText(hit) || `Match ${hitIndex + 1}`;
                                const pageSuffix = pageLabel(hit.pageIndex + 1);
                                const isLabel = hit.locateRole === 'label';
                                const roleLabel = isLabel
                                    ? 'Field label'
                                    : (isSsnField ? 'Pattern match' : 'Format match');
                                const showRoleIndex = entries.length > 1;
                                const head = showRoleIndex ? `${roleLabel} ${i + 1}` : roleLabel;
                                return renderFieldRow(likelyHelpers[0], {
                                    helper: true,
                                    status: 'found',
                                    rowKey: `${field.hint.id}:likely:${hitIndex}`,
                                    activeToken: `${field.hint.id}:likely:${hitIndex}`,
                                    labelOverride: pageSuffix ? `${head} · ${pageSuffix}` : head,
                                    valueOverride: matchText,
                                    titleOverride: isLabel
                                        ? `Field label on page: ${matchText}`
                                        : (isSsnField
                                            ? `Pattern match (not the expected SSN): ${matchText}`
                                            : `Format match (not the expected value): ${matchText}`),
                                    hideCount: true,
                                    onActivate: () => props.activateLikelyHit(field, likelyHelpers, hitIndex)
                                });
                            })}
                        </div>
                    ) : null}
                </div>
            );
        }
        return (
            <div
                key={field.hint.id}
                className={[
                    'dvFieldGroup',
                    status === 'missing' ? 'dvFieldGroupMiss' : ''
                ].filter(Boolean).join(' ')}
            >
                {renderFieldRow(field, { status })}
            </div>
        );
    };

    return (
        <aside className="dvLocate">
            {props.filePager && (
                <div className="dvLocateBlock dvFilePager">
                    <button
                        type="button"
                        className="dvFilePagerBtn"
                        disabled={props.filePager.total <= 1 || !props.filePager.canPrev || !!props.filePager.loading}
                        aria-label={`Previous ${props.filePager.label.toLowerCase()}`}
                        onClick={props.filePager.onPrev}
                    >
                        ‹ Previous
                    </button>
                    <span className="dvFilePagerLabel">
                        {props.filePager.label} {props.filePager.current} of {props.filePager.total}
                    </span>
                    <button
                        type="button"
                        className="dvFilePagerBtn"
                        disabled={props.filePager.total <= 1 || !props.filePager.canNext || !!props.filePager.loading}
                        aria-label={`Next ${props.filePager.label.toLowerCase()}`}
                        onClick={props.filePager.onNext}
                    >
                        Next ›
                    </button>
                </div>
            )}
            <div className="dvLocateBlock">
                <div className="dvLocateHeadingRow">
                    <div className="dvLocateHeading">Search</div>
                    {props.allowOcr && (props.mimeKind === 'image' || props.mimeKind === 'pdf') && (
                        <button
                            type="button"
                            className="dvRescanBtn"
                            disabled={props.ocrRunning || (props.mimeKind === 'image' ? !props.displayUrl : !props.hasPdf)}
                            aria-label="Rescan text"
                            title={props.mimeKind === 'pdf' ? 'Scan text on this page' : 'Rescan text from this image'}
                            onClick={() => { props.runScan(); }}
                        >
                            {props.ocrRunning
                                ? `${Math.round(props.ocrProgress * 100)}%`
                                : 'Rescan'}
                        </button>
                    )}
                </div>
                {props.allowOcr && (props.mimeKind === 'image' || props.mimeKind === 'pdf') && (props.ocrRunning || props.ocrNote) && (
                    <div className="dvLocateNote" aria-live="polite">
                        {props.ocrRunning ? (props.ocrNote || 'Scanning…') : props.ocrNote}
                    </div>
                )}
                <form
                    className="dvSearchForm"
                    onSubmit={e => {
                        e.preventDefault();
                        props.runCustomSearch();
                    }}
                >
                    <input
                        ref={searchInputRef}
                        className="dvSearchInput"
                        type="text"
                        value={props.customQuery}
                        disabled={!props.canSearch}
                        placeholder={props.canSearch ? 'Search…' : (props.ocrRunning ? 'Scanning…' : 'Not Found')}
                        onChange={e => props.setCustomQuery(e.target.value)}
                        onMouseDown={e => e.stopPropagation()}
                    />
                    <button type="submit" className="dvSearchSubmit" disabled={!props.canSearch}>
                        Find
                    </button>
                </form>
                {props.searchStatus && props.locateSource === 'custom' && props.hits.length === 0 && (
                    <div className="dvLocateNote">{props.searchStatus}</div>
                )}
                {!props.canSearch && props.mimeKind === 'pdf' && !props.hasText && !props.ocrRunning && (
                    <div className="dvLocateNote">
                        {props.allowOcr
                            ? 'This PDF has no text layer. Use Rescan to scan the current page.'
                            : 'This PDF has no searchable text.'}
                    </div>
                )}
                {!props.canSearch && props.mimeKind === 'image' && !props.allowOcr && (
                    <div className="dvLocateNote">Image has no searchable text.</div>
                )}
                {!props.canSearch && props.mimeKind === 'image' && props.allowOcr && !props.ocrRunning && !props.textReady && !props.ocrNote && (
                    <div className="dvLocateNote">Preparing text…</div>
                )}
            </div>

            {props.requiredFields.length > 0 && (
                <div className="dvLocateBlock">
                    <div className="dvLocateHeading">
                        {props.matchedLabel}
                        {props.verifyProgressLabel}
                    </div>
                    <div className="dvFieldList">
                        {props.requiredFields.map(renderFieldGroup)}
                    </div>
                </div>
            )}

            {props.orphanSupporting.length > 0 && (
                <div className="dvLocateBlock">
                    <div className="dvLocateHeading">
                        On page · {props.orphanSupporting.length}
                    </div>
                    <div className="dvFieldList">
                        {props.orphanSupporting.map(f => renderFieldRow(f, { helper: true }))}
                    </div>
                </div>
            )}

            {props.hits.length > 0 && (
                <div className="dvLocateBlock dvLocateBlockGrow dvLocateMatches">
                    <div className="dvLocateHeading">
                        Matches{props.activeLabel ? ` · ${props.activeLabel}` : ''}
                    </div>
                    <div className="dvMatchList">
                        {props.hits.map((hit, idx) => (
                            <button
                                key={`${hit.pageIndex}-${hit.matchIndex}-${hit.start}`}
                                type="button"
                                className={`dvMatchRow${idx === props.activeIndex ? ' dvMatchRowActive' : ''}`}
                                onClick={() => props.activateHit(idx, true)}
                            >
                                {showPageLabels && (
                                    <div className="dvMatchPage">Page {hit.pageIndex + 1}</div>
                                )}
                                <div className="dvMatchSnippet">{snippetNodes(hit)}</div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </aside>
    );
}
